import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, ArrowUp, ArrowDown, Filter, EyeOff, Ban } from 'lucide-react';
import { Label } from '@/components/atoms/texts/label';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import OnboardingCandidateItem from '@/components/molecules/onboarding-management/OnboardingCandidateItem';
import { ViewToggle } from '@/components/molecules/onboarding-management/ViewToggle';
import InViewComponent from '@/components/organisms/InViewComponent';
import { Button } from '@/components/molecules/buttons/button';

const STEP_TAB_IDS = {
    ALL: 'ALL',
    VALIDATION: 'VALIDATION',
    CONTRACT: 'CONTRACT',
    APPOINTMENT: 'APPOINTMENT',
    OTHERS: 'OTHERS',
};

const TAB_TO_STEPS = {
    [STEP_TAB_IDS.VALIDATION]: ['SYNAPSE_VALIDATION'],
    [STEP_TAB_IDS.CONTRACT]: ['CONTRACT_GENERATION'],
    [STEP_TAB_IDS.APPOINTMENT]: ['OFFICE_APPOINTMENT'],
    [STEP_TAB_IDS.OTHERS]: [],
};

const TRACKED_STEPS = [
    ...TAB_TO_STEPS[STEP_TAB_IDS.VALIDATION],
    ...TAB_TO_STEPS[STEP_TAB_IDS.CONTRACT],
    ...TAB_TO_STEPS[STEP_TAB_IDS.APPOINTMENT],
    ...TAB_TO_STEPS[STEP_TAB_IDS.OTHERS],
];

const STEP_FILTER_OPTIONS = [
    'PERSONAL_INFO',
    'DOCUMENTS',
    'CURRENT_MISSION',
    'SYNAPSE_VALIDATION',
    'ADMINISTRATIVE_INFO',
    'CONTRACT_GENERATION',
    'OFFICE_APPOINTMENT',
    'COMPLETE',
];

// Miroir de CandidatesRequestDto.refused : null = tous, false = exclus, true = uniquement.
const REFUSED_FILTER_OPTIONS = [
    { id: 'include', value: null },
    { id: 'exclude', value: false },
    { id: 'only', value: true },
];

const isNameSort = (sortOrder) => sortOrder === 'LASTNAME_ASC' || sortOrder === 'LASTNAME_DESC';
const isDateSort = (sortOrder) => sortOrder === 'CREATED_ASC' || sortOrder === 'CREATED_DESC';

const OnboardingCandidatesPanel = ({
    candidates,
    loading,
    error,
    selectedCandidateUuid,
    searchString,
    sortOrder,
    hasNextPage,
    stepFilter = null,
    stepFilterMode = 'single',
    stepCounts = [],
    stepCountsLoading = false,
    stepCountsError = null,
    refusedFilter = null,
    onRefusedFilterChange = () => { },
    onSearchChange,
    onSortOrderChange,
    onStepFilterChange = () => { },
    onSelectCandidate,
    onNextPage,
    autoSelectFirstCandidate = true,
}) => {
    const [t] = useTranslation('global');
    const [activeStepTab, setActiveStepTab] = useState(STEP_TAB_IDS.ALL);
    const [viewMode, setViewMode] = useState('list');
    const searchInputRef = useRef(null);
    const isMultiStepFilter = stepFilterMode === 'multiple';
    const selectedSteps = Array.isArray(stepFilter)
        ? stepFilter
        : (stepFilter ? [stepFilter] : []);
    const hasStepFilter = selectedSteps.length > 0;
    const activeIsName = isNameSort(sortOrder);
    const activeIsDate = isDateSort(sortOrder);
    const isAscending = sortOrder === 'LASTNAME_ASC' || sortOrder === 'CREATED_ASC';

    const normalizedSearch = searchString.trim().toLowerCase();

    const searchFilteredCandidates = useMemo(() => {
        if (!normalizedSearch) {
            return candidates;
        }

        return candidates.filter((candidate) => {
            const searchableValues = [
                candidate.firstname,
                candidate.lastname,
                candidate.email,
                candidate.phoneNumber,
            ];

            return searchableValues.some((value) => {
                return String(value || '').toLowerCase().includes(normalizedSearch);
            });
        });
    }, [candidates, normalizedSearch]);

    const filterByTab = (candidateList, tabId) => {
        if (tabId === STEP_TAB_IDS.ALL) {
            return candidateList;
        }

        if (tabId === STEP_TAB_IDS.OTHERS) {
            return candidateList.filter((candidate) => !TRACKED_STEPS.includes(candidate.step));
        }

        const allowedSteps = TAB_TO_STEPS[tabId] || [];

        return candidateList.filter((candidate) => allowedSteps.includes(candidate.step));
    };

    const filteredCandidates = useMemo(() => {
        return filterByTab(searchFilteredCandidates, activeStepTab);
    }, [activeStepTab, searchFilteredCandidates]);

    const refusedFilterId = REFUSED_FILTER_OPTIONS.find((option) => option.value === refusedFilter)?.id || 'include';

    // Par etape : count = tous les candidats, refusalCount = ceux refuses parmi eux.
    const stepCountByStep = useMemo(() => {
        return (stepCounts || []).reduce((accumulator, item) => {
            if (!item?.step) {
                return accumulator;
            }

            accumulator[item.step] = {
                count: Number(item.count || 0),
                refusalCount: Number(item.refusalCount || 0),
            };
            return accumulator;
        }, {});
    }, [stepCounts]);

    // Compteur affiche selon le filtre refused envoye au backend.
    const getDisplayedCount = ({ count, refusalCount }) => {
        if (refusedFilter === true) return refusalCount;
        if (refusedFilter === false) return Math.max(count - refusalCount, 0);
        return count;
    };

    const sumStepCounts = (steps) => {
        return steps.reduce((total, step) => ({
            count: total.count + (stepCountByStep[step]?.count || 0),
            refusalCount: total.refusalCount + (stepCountByStep[step]?.refusalCount || 0),
        }), { count: 0, refusalCount: 0 });
    };

    const tabItems = useMemo(() => {
        const allSteps = Object.keys(stepCountByStep);
        const tabSteps = [
            { id: STEP_TAB_IDS.ALL, labelKey: 'management.onboarding.filters.stepTabs.all', steps: allSteps },
            { id: STEP_TAB_IDS.VALIDATION, labelKey: 'management.onboarding.filters.stepTabs.validation', steps: TAB_TO_STEPS[STEP_TAB_IDS.VALIDATION] },
            { id: STEP_TAB_IDS.CONTRACT, labelKey: 'management.onboarding.filters.stepTabs.contract', steps: TAB_TO_STEPS[STEP_TAB_IDS.CONTRACT] },
            { id: STEP_TAB_IDS.APPOINTMENT, labelKey: 'management.onboarding.filters.stepTabs.appointment', steps: TAB_TO_STEPS[STEP_TAB_IDS.APPOINTMENT] },
            { id: STEP_TAB_IDS.OTHERS, labelKey: 'management.onboarding.filters.stepTabs.others', steps: allSteps.filter((step) => !TRACKED_STEPS.includes(step)) },
        ];

        return tabSteps.map(({ steps, ...tab }) => {
            const counts = sumStepCounts(steps);
            return { ...tab, count: getDisplayedCount(counts), refusalCount: counts.refusalCount };
        });
    }, [stepCountByStep, refusedFilter]);

    const shouldShowStepCounters = !stepCountsError;

    const handleNameFilterClick = () => {
        if (!activeIsName) {
            onSortOrderChange('LASTNAME_ASC');
            return;
        }

        onSortOrderChange(sortOrder === 'LASTNAME_ASC' ? 'LASTNAME_DESC' : 'LASTNAME_ASC');
    };

    const handleDateFilterClick = () => {
        if (!activeIsDate) {
            onSortOrderChange('CREATED_DESC');
            return;
        }

        onSortOrderChange(sortOrder === 'CREATED_DESC' ? 'CREATED_ASC' : 'CREATED_DESC');
    };

    const renderInlineSortFilter = () => (
        <div className="flex items-center gap-2 text-sm">
            <button
                type="button"
                onClick={handleNameFilterClick}
                className={[
                    'hover:text-primary transition-colors',
                    activeIsName ? 'font-bold text-night' : 'text-greyStrong',
                ].join(' ')}
            >
                <Label>{t('management.onboarding.filters.name')}</Label>
                {activeIsName && (
                    <span className="ml-1 inline-flex align-middle">
                        {isAscending ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                    </span>
                )}
            </button>

            <span className="text-greyStrong">•</span>

            <button
                type="button"
                onClick={handleDateFilterClick}
                className={[
                    'hover:text-primary transition-colors',
                    activeIsDate ? 'font-bold text-night' : 'text-greyStrong',
                ].join(' ')}
            >
                <Label>{t('management.onboarding.filters.date')}</Label>
                {activeIsDate && (
                    <span className="ml-1 inline-flex align-middle">
                        {isAscending ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                    </span>
                )}
            </button>
        </div>
    );

    const renderStepFilter = () => {
        const handleSingleStepChange = (stepValue) => {
            if (stepValue === STEP_TAB_IDS.ALL) {
                onStepFilterChange(null);
                return;
            }
            onStepFilterChange(stepValue);
        };

        const handleMultiStepToggle = (stepValue, checked) => {
            const nextSelectedSteps = checked
                ? [...selectedSteps, stepValue]
                : selectedSteps.filter((step) => step !== stepValue);

            onStepFilterChange(nextSelectedSteps.length > 0 ? nextSelectedSteps : null);
        };

        return (
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button 
                    style="dressedGhost" 
                    leftIcon={<Filter  />}
                    showLeftIcon
                    className="hover:bg-medium rounded-sm"
                    sizePadding="firstDegree"
                    text={hasStepFilter
                                ? (isMultiStepFilter
                                    ? `management.onboarding.step.${selectedSteps[0]}}${selectedSteps.length > 1 ? ` +${selectedSteps.length - 1}` : ''}`
                                    : `management.onboarding.step.${selectedSteps[0]}`)
                                : `management.onboarding.filters.stepTabs.all`}
                    />
                        
                
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                    {isMultiStepFilter ? (
                        <>
                            <DropdownMenuCheckboxItem
                                checked={!hasStepFilter}
                                onCheckedChange={(checked) => {
                                    if (checked) {
                                        onStepFilterChange(null);
                                    }
                                }}
                            >
                                <Label message="management.onboarding.filters.stepTabs.all" className="w-full" />
                            </DropdownMenuCheckboxItem>

                            {STEP_FILTER_OPTIONS.map((step) => (
                                <DropdownMenuCheckboxItem
                                    key={step}
                                    checked={selectedSteps.includes(step)}
                                    onCheckedChange={(checked) => handleMultiStepToggle(step, checked)}
                                >
                                    <Label className="w-full">
                                        {t(`management.onboarding.step.${step}`)}
                                    </Label>
                                </DropdownMenuCheckboxItem>
                            ))}
                        </>
                    ) : (
                        <DropdownMenuRadioGroup
                            value={selectedSteps[0] || STEP_TAB_IDS.ALL}
                            onValueChange={handleSingleStepChange}
                        >
                            <DropdownMenuRadioItem value={STEP_TAB_IDS.ALL}>
                                <Label message="management.onboarding.filters.stepTabs.all" className="w-full" />
                            </DropdownMenuRadioItem>

                            {STEP_FILTER_OPTIONS.map((step) => (
                                <DropdownMenuRadioItem key={step} value={step}>
                                    <Label className="w-full">
                                        {t(`management.onboarding.step.${step}`)}
                                    </Label>
                                </DropdownMenuRadioItem>
                            ))}
                        </DropdownMenuRadioGroup>
                    )}

                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>
                        <Label size="small" color="grey" message="management.onboarding.filters.refusedFilter.label" />
                    </DropdownMenuLabel>
                    <DropdownMenuRadioGroup
                        value={refusedFilterId}
                        onValueChange={(id) => onRefusedFilterChange(REFUSED_FILTER_OPTIONS.find((option) => option.id === id)?.value ?? null)}
                    >
                        {REFUSED_FILTER_OPTIONS.map((option) => (
                            <DropdownMenuRadioItem key={option.id} value={option.id}>
                                <Label message={`management.onboarding.filters.refusedFilter.${option.id}`} className="w-full" />
                            </DropdownMenuRadioItem>
                        ))}
                    </DropdownMenuRadioGroup>
                </DropdownMenuContent>
            </DropdownMenu>
        );
    };

    useEffect(() => {
        requestAnimationFrame(() => {
            searchInputRef.current?.focus();
        });
    }, []);

    useEffect(() => {
        if (stepFilter) {
            setActiveStepTab(STEP_TAB_IDS.ALL);
        }
    }, [stepFilter]);

    useEffect(() => {
        if (!autoSelectFirstCandidate) {
            return;
        }

        if (filteredCandidates.length === 0) {
            return;
        }

        const hasSelectedCandidateInFilteredList = filteredCandidates.some((candidate) => {
            return candidate.uuid === selectedCandidateUuid;
        });

        if (!hasSelectedCandidateInFilteredList) {
            onSelectCandidate(filteredCandidates[0].uuid);
        }
    }, [autoSelectFirstCandidate, filteredCandidates, onSelectCandidate, selectedCandidateUuid]);

    const handleLoadNextSlice = () => {
        if (hasNextPage && !loading) {
            onNextPage();
        }
    };

    return (
        <div className="flex flex-col min-h-0">
            <div className=" overflow-hidden">
                <div className=" flex items-center justify-between gap-secondDegree border-b">
                    <div className="relative w-full p-1">
                        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-grey" />
                        <Input
                            ref={searchInputRef}
                            type="text"
                            className="pl-9 rounded-sm h-11"
                            placeholder={t('management.onboarding.filters.searchPlaceholder')}
                            value={searchString}
                            onChange={(event) => onSearchChange(event.target.value)}
                        />
                    </div>

                    <div className="shrink-0">
                        <div className="flex items-center gap-2">
                            {renderInlineSortFilter()}
                            {renderStepFilter()}
                        </div>
                    </div>

                </div>

                <div className='flex justify-between items-center'> 
                    <Tabs value={activeStepTab} onValueChange={setActiveStepTab}>
                    <TabsList className="    bg-transparent p-0 gap-0">
                        {tabItems.map((tab) => (
                            <TabsTrigger
                                key={tab.id}
                                value={tab.id}
                                className="hover:bg-hoverLight"
                                disabled={shouldShowStepCounters && !stepCountsLoading && tab.count === 0}
                            >
                                <Label>{t(tab.labelKey)}</Label>
                                {shouldShowStepCounters && (
                                    <div className={`h-5 min-w-[22px] px-1 rounded-full border text-xs inline-flex items-center justify-center ${refusedFilter === true ? 'bg-error20 border-error text-error' : 'bg-lighter'}`}>
                                        <Label>{stepCountsLoading ? '-' : tab.count}</Label>
                                    </div>
                                )}
                                {shouldShowStepCounters && !stepCountsLoading && refusedFilter === null && tab.refusalCount > 0 && (
                                    <div
                                        className="h-5 aspect-square px-1 leading-none rounded-full bg-error20 text-error inline-flex justify-center items-center gap-0.5"
                                        title={t('management.onboarding.filters.refusedInStep', { count: tab.refusalCount })}
                                    >
                                        <Label>{tab.refusalCount}</Label>
                                    </div>
                                )}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
                    <div className="flex items-center gap-firstDegree">
                        {refusedFilter === false && (
                            <div className="flex items-center gap-1 rounded-full border border-warning bg-warning20 px-2 py-0.5">
                                <EyeOff className="h-3 w-3" />
                                <Label size="small" message="management.onboarding.filters.refusedExcluded" />
                            </div>
                        )}
                        {refusedFilter === true && (
                            <div className="flex  items-center justify-center gap-1 rounded-full border border-error  bg-error20 px-2 py-0.5 text-error">
                                <Label fontWeight="medium" message="management.onboarding.filters.refusedOnly" />
                            </div>
                        )}
                        <ViewToggle value={viewMode} onValueChange={setViewMode} />
                    </div>
                </div>
            </div>

            <div className="mt-secondDegree pb-thirdDegree min-h-0 flex-1 overflow-y-auto flex flex-col gap-firstDegree pr-1">
                {loading && <span className="loader" />}

                {!loading && error && (
                    <Label color="error" message="error.fetchUnsuccessfull" />
                )}

                {!loading && !error && filteredCandidates.length === 0 && (
                    <div className="h-full flex items-center justify-center border rounded-md p-secondDegree bg-lighter">
                        <div className="flex flex-col items-center gap-2">
                            <Label message="management.onboarding.empty.candidates" />

                            {hasStepFilter && (
                                <div className="flex items-center gap-2 rounded-full border border-warning bg-warning20 px-3 py-2">
                                    <Label>
                                        {t('management.onboarding.filters.stepFilteredView', {
                                            step: isMultiStepFilter
                                                ? `${t(`management.onboarding.step.${selectedSteps[0]}`)}${selectedSteps.length > 1 ? ` +${selectedSteps.length - 1}` : ''}`
                                                : t(`management.onboarding.step.${selectedSteps[0]}`),
                                        })}
                                    </Label>
                                    <Button style="dressedGhost" sizePadding="small" onClick={() => onStepFilterChange(null)} text="management.onboarding.filters.clearStepFilter">
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                <div className={viewMode === 'list' ? 'flex flex-col gap-firstDegree' : 'grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 xxl:grid-cols-4 gap-firstDegree'}>
                    {!loading && !error && filteredCandidates.map((candidate) => (
                        <OnboardingCandidateItem
                            key={candidate.uuid}
                            candidate={candidate}
                            isActive={selectedCandidateUuid === candidate.uuid}
                            onClick={() => onSelectCandidate(candidate.uuid)}
                            viewMode={viewMode}
                            dimRefused={refusedFilter !== true}
                        />
                    ))}
                </div>

            </div>

            {hasNextPage && (
                <div className="pt-firstDegree mt-firstDegree border-t">
                    <InViewComponent inView={handleLoadNextSlice} />
                </div>
            )}
        </div>
    );
};

export default OnboardingCandidatesPanel;
